<template>
  <div class="pp">
    <!-- Header -->
    <div class="pp-header">
      <div>
        <h2 class="pp-title">Pilotage Planning</h2>
        <p class="pp-sub">Tide-window planning for berthing &amp; unberthing</p>
      </div>
      <div class="pp-source">
        <div class="src-field">
          <span class="src-label">Tide data source</span>
          <span class="src-current">{{ currentSourceLabel }}</span>
        </div>
        <div class="src-actions">
          <el-upload
            action="#"
            :show-file-list="false"
            accept=".csv"
            :http-request="handleUpload"
            :before-upload="beforeUpload"
          >
            <el-button size="small" type="primary" icon="el-icon-upload2">Upload CSV</el-button>
          </el-upload>
        </div>
      </div>
    </div>

    <div class="pp-body">
      <!-- Form -->
      <section class="card form-card">
        <header class="card-head">
          <span class="dot"></span>
          <h3>Vessel details</h3>
        </header>

        <div class="form-grid">
          <div class="fld">
            <label>Job Type</label>
            <el-select v-model="form.jobType" size="small" @change="onJobOrJettyChange">
              <el-option label="Berthing" :value="0" />
              <el-option label="Unberthing" :value="1" />
            </el-select>
          </div>

          <div class="fld">
            <label>Jetty</label>
            <el-select v-model="form.jetty" size="small" @change="onJobOrJettyChange">
              <el-option v-for="j in jettyOptions" :key="j" :label="j" :value="j" />
            </el-select>
          </div>

          <div class="fld">
            <label class="lbl-link">
              Anchorages
              <a
                class="chartlet-link"
                href="#"
                title="View anchorages chartlet"
                @click.prevent="openAnchoragesChartlet"
              ><i class="el-icon-picture-outline"></i> chartlet</a>
            </label>
            <el-select v-model="form.anchorages" size="small" :disabled="!anchoragesEnabled">
              <el-option label="West" :value="1" />
              <el-option label="East" :value="2" />
            </el-select>
          </div>

          <div class="fld">
            <label>Unberthing</label>
            <el-select v-model="form.unberthing" size="small" :disabled="!unberthingEnabled">
              <el-option label="Port" :value="1" />
              <el-option label="Starboard" :value="2" />
            </el-select>
          </div>

          <div class="fld fld-date">
            <label>Date</label>
            <div class="date-row">
              <el-select v-model="form.year" size="small"><el-option v-for="y in years" :key="y" :label="y" :value="y" /></el-select>
              <el-select v-model="form.month" size="small"><el-option v-for="m in months" :key="m.v" :label="m.l" :value="m.v" /></el-select>
              <el-select v-model="form.day" size="small"><el-option v-for="d in days" :key="d" :label="d" :value="d" /></el-select>
            </div>
          </div>

          <div class="fld">
            <label>No. of Days</label>
            <el-select v-model="form.nDay" size="small">
              <el-option v-for="n in [0,1,2,3,4,5]" :key="n" :label="n" :value="n" />
            </el-select>
          </div>

          <div class="fld">
            <label>Draft (m)</label>
            <el-input v-model="form.draft" size="small" :disabled="draftDisabled" :placeholder="draftDisabled ? 'N.A' : 'e.g. 12.5'" />
          </div>

          <div class="fld">
            <label>LOA (m)</label>
            <el-input v-model="form.loa" size="small" placeholder="50 - 400" />
          </div>

          <div class="fld fld-switch">
            <label>Berth Planning</label>
            <el-switch v-model="form.berthPlanning" active-color="#008CDB" :disabled="!berthPlanningAvailable" />
          </div>
        </div>

        <!-- Berth planning -->
        <transition name="fade">
          <div v-if="berthPlanningAvailable && form.berthPlanning" class="subcard">
            <div class="bp-group">
              <div class="bp-group-head">
                <span class="bp-badge bp-badge-a">A</span> Vessel A
              </div>
              <div class="form-grid">
                <div class="fld"><label>BCM (m)</label><el-input v-model="form.bcm" size="small" placeholder="25 - 220" /></div>
                <div class="fld">
                  <label>Product</label>
                  <el-select v-model="form.product" size="small"><el-option v-for="p in products" :key="p.v" :label="p.l" :value="p.v" /></el-select>
                </div>
              </div>
            </div>

            <div class="bp-divider"></div>

            <div class="bp-group">
              <div class="bp-group-head">
                <span class="bp-badge bp-badge-b">B</span> Vessel B
                <el-checkbox v-model="form.berthed" class="bp-berthed">Already berthed</el-checkbox>
              </div>
              <div class="form-grid">
                <div class="fld">
                  <label>Jetty</label>
                  <el-select v-model="form.jettyB" size="small" :disabled="form.jetty === 11">
                    <el-option v-for="jb in jettyBOptions" :key="jb" :label="jb" :value="jb" />
                  </el-select>
                </div>
                <div class="fld"><label>LOA (m)</label><el-input v-model="form.loaB" size="small" placeholder="50 - 400" /></div>
                <div class="fld"><label>BCM (m)</label><el-input v-model="form.bcmB" size="small" placeholder="25 - 220" /></div>
                <div class="fld">
                  <label>Product</label>
                  <el-select v-model="form.productB" size="small"><el-option v-for="p in products" :key="p.v" :label="p.l" :value="p.v" /></el-select>
                </div>
                <div v-if="form.berthed" class="fld">
                  <label>Berthing</label>
                  <el-select v-model="form.berthingB" size="small">
                    <el-option label="Port" :value="1" />
                    <el-option label="Starboard" :value="2" />
                  </el-select>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <footer class="card-foot">
          <el-button type="primary" :loading="loading" icon="el-icon-data-analysis" @click="submit">Generate Plan</el-button>
        </footer>
      </section>

      <!-- Result -->
      <section class="card result-card">
        <header class="card-head">
          <span class="dot"></span>
          <h3>Tide Window</h3>
          <span v-if="result" class="summary">{{ resultSummary }}</span>
        </header>

        <template v-if="result">
          <div class="legend">
            <span class="legend-item"><i class="sw ok"></i> Permissible</span>
            <span class="legend-item"><i class="sw blocked"></i> Not permissible</span>
            <span class="legend-item"><i class="sw neg-key"></i> Negative tide (falling)</span>
            <span class="src-tag">Source: {{ result.tableName }}</span>
          </div>

          <div class="grid-scroll">
            <table class="tide-grid">
              <thead>
                <tr>
                  <th class="date-col">Date</th>
                  <th v-for="h in result.hours" :key="h">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in result.rows" :key="ri">
                  <td class="date-col">{{ row.date }}</td>
                  <td v-for="(c, ci) in row.cells" :key="ci" :class="c.ok ? 'ok' : 'blocked'">
                    <span :class="{ neg: c.negative }">{{ c.value }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="result.berthPlanning" class="berth-plan">
            <div class="bp-result-title">Berth Planning</div>
            <p v-if="result.berthPlanning.message" class="bp-result-msg">{{ result.berthPlanning.message }}</p>
            <p v-if="result.berthPlanning.header" class="bp-result-header">{{ result.berthPlanning.header }}</p>
            <div v-if="bpLines.length" class="bp-result-list">
              <div
                v-for="(l, li) in bpLines"
                :key="li"
                class="bp-result-row"
                :class="l.ok ? 'is-ok' : 'is-no'"
              >
                <i :class="l.ok ? 'el-icon-circle-check' : 'el-icon-circle-close'"></i>
                <span class="bp-result-desc">{{ l.desc }}<template v-if="l.dist"> - the separation distance between vessels is <strong class="bp-result-dist">{{ l.dist }}</strong></template></span>
              </div>
            </div>
          </div>

          <div v-if="resultInput" class="input-recap">
            <div class="ir-title">Input</div>
            <div class="ir-row">
              <span class="ir-tag ir-tag-a">Vessel A</span>
              <span v-for="(it, i) in resultInput.vesselA" :key="'a' + i" class="ir-item">{{ it.label }}: <b>{{ it.value }}</b></span>
            </div>
            <div v-if="resultInput.vesselB.length" class="ir-row">
              <span class="ir-tag ir-tag-b">Vessel B</span>
              <span v-for="(it, i) in resultInput.vesselB" :key="'b' + i" class="ir-item">{{ it.label }}: <b>{{ it.value }}</b></span>
            </div>
          </div>
        </template>

        <div v-else class="empty">
          <i class="el-icon-data-line"></i>
          <p>Fill in the vessel details and hit <b>Generate Plan</b> to see the tide window.</p>
        </div>
      </section>
    </div>

    <!-- Upload dialog: state the year this CSV covers -->
    <el-dialog title="Upload tide table CSV" :visible.sync="uploadVisible" width="420px">
      <el-form label-width="70px" size="small">
        <el-form-item label="Year" required>
          <el-select v-model="uploadForm.year" placeholder="Select year" style="width:100%">
            <el-option v-for="y in years" :key="y" :label="y" :value="y" />
          </el-select>
        </el-form-item>
        <el-form-item label="Name">
          <el-input v-model="uploadForm.name" placeholder="Tide table name" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="uploadVisible = false">Cancel</el-button>
        <el-button size="small" type="primary" @click="confirmUpload">Upload</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getCellStyle } from "@/utils/getCellStyle";
import anchoragesChartlet from "@/assets/anchorages-chartlet.jpg";
import {
  computePilotagePlan,
  getPilotageTideTables,
  uploadPilotageTideTable,
  setActivePilotageTideTable,
} from "@/services/index";

const NO_DRAFT_JETTIES = [5, 6, 7, 8, 9, 10, 15, 16, 17];

export default {
  name: "Pilotage Planning New",
  data() {
    const now = new Date();
    return {
      tables: [],
      selectedTableId: null,
      uploadVisible: false,
      pendingFile: null,
      uploadForm: { year: String(now.getFullYear()), name: "" },
      loading: false,
      initDone: false,
      result: null,
      resultSummary: "",
      resultInput: null,
      tableHeaderStyle: {
        height: "50px",
        background: "#fff",
        "border-bottom": "2px solid #D6D9E1",
        color: "#1D1B20",
      },
      jettyOptions: [1, 2, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 16, 17],
      jettyBOptions: ["HZ", "11", "13"],
      products: [
        { v: 1, l: "FO" },
        { v: 2, l: "GO" },
        { v: 3, l: "JET" },
        { v: 4, l: "MG" },
      ],
      months: [
        { v: "01", l: "January" }, { v: "02", l: "February" }, { v: "03", l: "March" },
        { v: "04", l: "April" }, { v: "05", l: "May" }, { v: "06", l: "June" },
        { v: "07", l: "July" }, { v: "08", l: "August" }, { v: "09", l: "September" },
        { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" },
      ],
      form: {
        jobType: 0,
        jetty: 1,
        anchorages: 1,
        unberthing: 1,
        year: String(now.getFullYear()),
        month: String(now.getMonth() + 1).padStart(2, "0"),
        day: String(now.getDate()).padStart(2, "0"),
        nDay: 0,
        draft: "",
        loa: "",
        berthPlanning: false,
        jettyB: "13",
        loaB: "",
        bcm: "",
        bcmB: "",
        product: 1,
        productB: 1,
        berthingB: 1,
        berthed: true,
      },
    };
  },
  computed: {
    ...mapState(["userInfo"]),
    years() {
      const y = new Date().getFullYear();
      return [y - 1, y, y + 1, y + 2, y + 3].map(String);
    },
    days() {
      return Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
    },
    selectedTable() {
      return this.tables.find((t) => t.id === this.selectedTableId);
    },
    currentSourceLabel() {
      const t = this.selectedTable;
      if (!t) return "No tide data source";
      return t.year ? `${t.name} (${t.year})` : t.name;
    },
    isBerthing() {
      return this.form.jobType === 0;
    },
    draftDisabled() {
      return NO_DRAFT_JETTIES.includes(this.form.jetty);
    },
    anchoragesEnabled() {
      return this.isBerthing && [1, 2, 3].includes(this.form.jetty);
    },
    unberthingEnabled() {
      return !this.isBerthing && [1, 2, 3].includes(this.form.jetty);
    },
    berthPlanningAvailable() {
      return this.isBerthing && [11, 13].includes(this.form.jetty);
    },
    bpLines() {
      const bp = this.result && this.result.berthPlanning;
      if (!bp || !bp.lines) return [];
      const sep = " - the separation distance between vessels is ";
      return bp.lines.map((l) => {
        const i = l.text.indexOf(sep);
        return i === -1
          ? { ok: l.ok, desc: l.text, dist: "" }
          : { ok: l.ok, desc: l.text.slice(0, i), dist: l.text.slice(i + sep.length) };
      });
    },
  },
  methods: {
    getCellStyle,
    openAnchoragesChartlet() {
      window.open(anchoragesChartlet, "_blank");
    },
    loadTables() {
      return getPilotageTideTables({ pageNum: 1, pageSize: 100 }).then((res) => {
        this.tables = res.rows || [];
      });
    },
    beforeUpload(file) {
      const isCsv = file.name.toLowerCase().endsWith(".csv");
      if (!isCsv) this.$message.error("Please upload a .csv file");
      return isCsv;
    },
    handleUpload(option) {
      // Capture the file and open a dialog so the user states the year (one CSV per year).
      this.pendingFile = option.file;
      this.uploadForm = {
        year: String(new Date().getFullYear()),
        name: option.file.name.replace(/\.csv$/i, ""),
      };
      this.uploadVisible = true;
    },
    confirmUpload() {
      if (!this.uploadForm.year) {
        this.$message.error("Please select the year this tide table covers");
        return;
      }
      uploadPilotageTideTable(this.pendingFile, this.uploadForm.name, this.uploadForm.year)
        .then((res) => {
          this.$message.success("Uploaded — now selected as the tide source");
          this.uploadVisible = false;
          this.pendingFile = null;
          const newId = res && res.data && res.data.id;
          this.loadTables().then(() => {
            if (newId) this.selectedTableId = newId;
          });
        })
        .catch((e) => {
          this.$message.error((e && e.msg) || "Upload failed");
        });
    },
    onJobOrJettyChange() {
      if (this.form.jetty === 11) this.form.jettyB = "13";
      if (!this.berthPlanningAvailable) this.form.berthPlanning = false;
      if (this.draftDisabled) this.form.draft = "";
    },
    validate() {
      const loa = parseFloat(this.form.loa);
      if (!(loa >= 50 && loa <= 400)) {
        this.$message.error("Please enter a valid LOA from 50 to 400 for Vessel A");
        return false;
      }
      if (!this.draftDisabled) {
        const d = parseFloat(this.form.draft);
        const max = [1, 2].includes(this.form.jetty) ? 22 : 16.5;
        if (!(d >= 2 && d <= max)) {
          this.$message.error(`Please enter a valid draft from 2 to ${max} for Vessel A`);
          return false;
        }
      }
      if (this.berthPlanningAvailable && this.form.berthPlanning) {
        const bcm = parseFloat(this.form.bcm);
        if (!(bcm >= 25 && bcm <= 220 && bcm < loa)) {
          this.$message.error("Vessel A - please enter a valid BCM from 25 to 220 (less than LOA)");
          return false;
        }
        const loaB = parseFloat(this.form.loaB);
        const bcmB = parseFloat(this.form.bcmB);
        if (!(loaB >= 50 && loaB <= 400)) {
          this.$message.error("Please enter a valid LOA from 50 to 400 for Vessel B");
          return false;
        }
        if (!(bcmB >= 25 && bcmB <= 220 && bcmB < loaB)) {
          this.$message.error("Vessel B - please enter a valid BCM from 25 to 220 (less than LOA)");
          return false;
        }
      }
      return true;
    },
    buildSummary() {
      const f = this.form;
      const job = f.jobType === 0 ? "Berthing" : "Unberthing";
      const mon = (this.months.find((m) => m.v === f.month) || {}).l || f.month;
      const parts = [job, `Jetty ${f.jetty}`, `${f.day} ${mon} ${f.year}`, `LOA ${f.loa}`];
      if (!this.draftDisabled && f.draft) parts.push(`Draft ${f.draft}`);
      return parts.join("  ·  ");
    },
    buildInput() {
      const f = this.form;
      const productLabel = (v) => (this.products.find((p) => p.v === v) || {}).l || "";
      const bp = this.berthPlanningAvailable && f.berthPlanning;

      const a = [];
      a.push({ label: "Job Type", value: f.jobType === 0 ? "Berthing" : "Un-berthing" });
      a.push({ label: "Jetty", value: String(f.jetty) });
      if (this.anchoragesEnabled) {
        a.push({ label: "Anchorages", value: f.anchorages === 1 ? "West" : "East" });
      }
      if (this.unberthingEnabled) {
        a.push({ label: "Unberthing", value: f.unberthing === 1 ? "Port" : "Starboard" });
      }
      a.push({ label: "Date", value: `${f.day}/${f.month}/${f.year}` });
      a.push({ label: "No. of Days", value: String(f.nDay) });
      a.push({ label: "LOA", value: f.loa });
      a.push({ label: "Draft", value: this.draftDisabled ? "N.A" : f.draft });
      if (bp && f.bcm) a.push({ label: "BCM", value: f.bcm });
      if (bp) a.push({ label: "Product", value: productLabel(f.product) });

      const b = [];
      if (bp) {
        b.push({ label: "Jetty", value: String(f.jettyB) });
        if (f.loaB) b.push({ label: "LOA", value: f.loaB });
        if (f.bcmB) b.push({ label: "BCM", value: f.bcmB });
        b.push({ label: "Product", value: productLabel(f.productB) });
        if (f.berthed) {
          b.push({ label: "Berthing", value: f.berthingB === 1 ? "Port" : "Starboard" });
        }
      }
      return { vesselA: a, vesselB: b };
    },
    submit() {
      if (!this.selectedTableId) {
        this.$message.error("Please upload and select a tide data source first");
        return;
      }
      if (!this.validate()) return;
      const f = this.form;
      const payload = {
        tableId: this.selectedTableId,
        jobType: f.jobType,
        jetty: f.jetty,
        anchorages: this.anchoragesEnabled ? f.anchorages : 0,
        unberthing: this.unberthingEnabled ? f.unberthing : 0,
        year: f.year,
        month: f.month,
        day: f.day,
        nDay: f.nDay,
        draft: this.draftDisabled ? "N.A" : f.draft,
        loa: parseFloat(f.loa),
        berthPlanning: this.berthPlanningAvailable && f.berthPlanning ? 1 : 0,
        jettyB: f.jettyB,
        loaB: f.loaB ? parseFloat(f.loaB) : null,
        bcm: f.bcm ? parseFloat(f.bcm) : null,
        bcmB: f.bcmB ? parseFloat(f.bcmB) : null,
        product: f.product,
        productB: f.productB,
        berthingB: f.berthingB,
        berthed: f.berthed ? 1 : 0,
      };
      const summary = this.buildSummary();
      const input = this.buildInput();
      this.loading = true;
      computePilotagePlan(payload)
        .then((res) => {
          this.result = res.data;
          this.resultSummary = summary;
          this.resultInput = input;
        })
        .catch((e) => {
          this.$message.error((e && e.msg) || "Failed to compute");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    // Persist the chosen tide source server-side so it survives a page refresh.
    selectedTableId(val) {
      if (!this.initDone || !val) return;
      setActivePilotageTideTable(val).catch(() => {});
    },
  },
  mounted() {
    this.loadTables().then(() => {
      // Use the admin-selected active table, else the latest upload (list is sorted active-first, newest-first).
      const active = this.tables.find((t) => Number(t.isActive) === 1) || this.tables[0];
      this.selectedTableId = active ? active.id : null;
      this.$nextTick(() => {
        this.initDone = true;
      });
    });
  },
};
</script>

<style scoped lang="scss">
$primary: #008cdb;
$ink: #1d1b20;
$muted: #82889c;
$line: #ebeef5;

.pp {
  height: calc(100vh - 90px);
  overflow-y: auto;
  box-sizing: border-box;
  background: #f2f7fa;
  padding: 20px 22px 32px;
}

/* header */
.pp-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 16px;
}
.pp-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: $ink;
  letter-spacing: 0.2px;
}
.pp-sub {
  margin: 3px 0 0;
  font-size: 12.5px;
  color: $muted;
}
.pp-source {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  background: #fff;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 1px 2px rgba(16, 42, 67, 0.04);
}
.src-field {
  display: flex;
  align-items: center;
  gap: 10px;
}
.src-label {
  font-size: 12px;
  font-weight: 600;
  color: $ink;
}
.src-select {
  width: 240px;
}
.src-current {
  font-size: 13px;
  font-weight: 600;
  color: $ink;
}
.src-range {
  font-size: 12px;
  color: $muted;
  white-space: nowrap;
}
.src-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* body layout */
.pp-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

/* cards */
.card {
  background: #fff;
  border: 1px solid $line;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 42, 67, 0.04);
  overflow: hidden;
}
.card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid $line;
}
.card-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: $ink;
}
.card-head .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $primary;
}
.card-head .summary {
  margin-left: auto;
  font-size: 12px;
  color: $muted;
}

/* form */
.form-card .form-grid {
  padding: 18px;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 208px);
  justify-content: start;
  gap: 18px 16px;
}
@media (max-width: 520px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  .fld-date,
  .fld-anchorage {
    grid-column: auto;
  }
}
.fld {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fld label {
  font-size: 12px;
  color: $muted;
  font-weight: 500;
}
.fld ::v-deep .el-select,
.fld ::v-deep .el-input {
  width: 100%;
}
.fld-date {
  grid-column: span 2;
}
.date-row {
  display: flex;
  gap: 8px;
}
.date-row ::v-deep .el-select {
  flex: 1;
}
.fld-switch {
  justify-content: flex-start;
}
.lbl-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.chartlet-link {
  color: $primary;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.chartlet-link:hover {
  text-decoration: underline;
}

/* berth planning input */
.bp-group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: $ink;
  margin-bottom: 14px;
}
.bp-divider {
  height: 1px;
  background: #e6ecf2;
  margin: 16px 0;
}
.bp-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.bp-badge-a {
  background: $primary;
}
.bp-badge-b {
  background: #16a34a;
}
.bp-berthed {
  margin-left: auto;
}

.subcard {
  margin: 0 18px 4px;
  padding: 14px 16px;
  background: #f7fafc;
  border: 1px solid #e6ecf2;
  border-radius: 10px;
}
.subcard .form-grid {
  padding: 0;
  margin-bottom: 6px;
}
.subcard-title {
  font-size: 13px;
  font-weight: 600;
  color: $ink;
  margin: 4px 0 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.subcard-title.with-check {
  margin-top: 16px;
}

.card-foot {
  display: flex;
  justify-content: flex-end;
  padding: 14px 18px;
  border-top: 1px solid $line;
  background: #fbfdff;
}

/* legend */
.legend {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 14px 18px 6px;
  font-size: 12px;
  color: #4c565c;
  flex-wrap: wrap;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.sw {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
}
.sw.ok {
  background: #cef9cd;
  border: 1px solid #9fe89d;
}
.sw.blocked {
  background: #ffe6e1;
  border: 1px solid #ffc2b6;
}
.sw.neg-key {
  background: #fff;
  border: 1px solid #e0e0e0;
  color: #d40000;
  font-size: 10px;
  line-height: 12px;
  text-align: center;
}
.sw.neg-key::before {
  content: "-";
  color: #d40000;
  font-weight: 700;
}
.src-tag {
  margin-left: auto;
  color: $muted;
}

/* grid */
.grid-scroll {
  margin: 6px 18px 18px;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid $line;
}
.tide-grid {
  border-collapse: collapse;
  font-size: 12px;
  width: 100%;
}
.tide-grid th,
.tide-grid td {
  border: 1px solid #eef1f6;
  padding: 7px 8px;
  text-align: center;
  white-space: nowrap;
}
.tide-grid thead th {
  background: $primary;
  color: #fff;
  font-weight: 500;
  position: sticky;
  top: 0;
  z-index: 2;
}
.tide-grid th.date-col,
.tide-grid td.date-col {
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 96px;
}
.tide-grid td.date-col {
  background: #eaf4fb;
  color: $ink;
  font-weight: 600;
}
.tide-grid thead th.date-col {
  background: darken($primary, 6%);
  z-index: 3;
}
.tide-grid td.ok {
  background: #d6f8d4;
}
.tide-grid td.blocked {
  background: #ffe1db;
}
.tide-grid tbody tr:hover td.ok {
  background: #c2f2bf;
}
.tide-grid tbody tr:hover td.blocked {
  background: #ffd2c9;
}
.tide-grid td .neg {
  color: #d40000;
}

/* berth planning results */
.berth-plan {
  padding: 0 18px 18px;
}
.bp-result-title {
  font-size: 13px;
  font-weight: 600;
  color: $ink;
  margin: 4px 0 10px;
}
.bp-result-msg {
  color: $muted;
  margin: 6px 0;
}
.bp-result-header {
  color: $primary;
  font-weight: 600;
  margin: 6px 0 12px;
}
.bp-result-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 900px;
}
.bp-result-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 13px;
}
.bp-result-row i {
  font-size: 16px;
  flex: none;
}
.bp-result-row.is-ok {
  background: #f0faf1;
  border-color: #cdeccf;
}
.bp-result-row.is-no {
  background: #fdf1f0;
  border-color: #f6d5d1;
}
.bp-result-desc {
  flex: 1;
  color: #3a4149;
}
.bp-result-row.is-ok > i {
  color: #1f9d3a;
}
.bp-result-row.is-no > i {
  color: #d40000;
}
.bp-result-dist {
  font-weight: 700;
  white-space: nowrap;
}
.bp-result-row.is-ok .bp-result-dist {
  color: #1f7a34;
}
.bp-result-row.is-no .bp-result-dist {
  color: #b5352a;
}

/* input recap */
.input-recap {
  padding: 0 18px 18px;
}
.ir-title {
  font-size: 13px;
  font-weight: 600;
  color: $ink;
  margin: 4px 0 10px;
}
.ir-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 18px;
  padding: 10px 14px;
  border: 1px solid $line;
  border-radius: 8px;
  background: #fbfdff;
  margin-bottom: 8px;
}
.ir-tag {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  padding: 2px 8px;
  border-radius: 6px;
}
.ir-tag-a {
  background: $primary;
}
.ir-tag-b {
  background: #16a34a;
}
.ir-item {
  font-size: 12.5px;
  color: $muted;
}
.ir-item b {
  color: $ink;
  font-weight: 600;
}

/* empty state */
.empty {
  padding: 48px 18px 56px;
  text-align: center;
  color: $muted;
}
.empty i {
  font-size: 40px;
  color: #c7d3de;
}
.empty p {
  margin: 12px 0 0;
  font-size: 13px;
}

.muted {
  color: $muted;
}
.dialog-hint {
  margin: 0 0 12px;
  font-size: 12px;
}
.mgr-tag {
  margin-left: 6px;
}

/* transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
